import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Shop } from '@/types';
import { shopsApi } from '@/services/api';
import toast from 'react-hot-toast';

interface ShopsState {
  shops: Shop[];
  currentShop: Shop | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    total: number;
    pages: number;
  };
}

const initialState: ShopsState = {
  shops: [],
  currentShop: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pages: 0,
  },
};

// Async thunks
export const fetchShops = createAsyncThunk(
  'shops/fetchShops',
  async (_, { rejectWithValue }) => {
    try {
      const response = await shopsApi.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch shops');
    }
  }
);

export const fetchMyShop = createAsyncThunk(
  'shops/fetchMyShop',
  async (_, { rejectWithValue }) => {
    try {
      const response = await shopsApi.getMyShop();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch your shop');
    }
  }
);

export const fetchShopById = createAsyncThunk(
  'shops/fetchShopById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await shopsApi.getById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch shop');
    }
  }
);

export const createShop = createAsyncThunk(
  'shops/createShop',
  async (shopData: any, { rejectWithValue }) => {
    try {
      const response = await shopsApi.create(shopData);
      toast.success('Shop created successfully!');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to create shop';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateShop = createAsyncThunk(
  'shops/updateShop',
  async ({ id, shopData }: { id: number; shopData: any }, { rejectWithValue }) => {
    try {
      const response = await shopsApi.update(id, shopData);
      toast.success('Shop updated successfully');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to update shop';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateShopVisibility = createAsyncThunk(
  'shops/updateShopVisibility',
  async ({ id, isPublic }: { id: number; isPublic: boolean }, { rejectWithValue }) => {
    try {
      const response = await shopsApi.updateVisibility(id, isPublic);
      toast.success(`Shop ${isPublic ? 'published' : 'unpublished'} successfully`);
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to update shop visibility';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const rateShop = createAsyncThunk(
  'shops/rateShop',
  async ({ id, rating }: { id: number; rating: number }, { rejectWithValue }) => {
    try {
      const response = await shopsApi.rateShop(id, rating);
      toast.success('Shop rated successfully');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to rate shop';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const shopsSlice = createSlice({
  name: 'shops',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentShop: (state, action: PayloadAction<Shop | null>) => {
      state.currentShop = action.payload;
    },
    clearCurrentShop: (state) => {
      state.currentShop = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Shops
      .addCase(fetchShops.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShops.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shops = action.payload;
        state.error = null;
      })
      .addCase(fetchShops.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch My Shop
      .addCase(fetchMyShop.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyShop.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentShop = action.payload;
        state.error = null;
      })
      .addCase(fetchMyShop.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Shop by ID
      .addCase(fetchShopById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShopById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentShop = action.payload;
        state.error = null;
      })
      .addCase(fetchShopById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create Shop
      .addCase(createShop.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createShop.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shops.unshift(action.payload);
        state.currentShop = action.payload;
        state.error = null;
      })
      .addCase(createShop.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Shop
      .addCase(updateShop.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateShop.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.shops.findIndex(shop => shop.id === action.payload.id);
        if (index !== -1) {
          state.shops[index] = action.payload;
        }
        if (state.currentShop?.id === action.payload.id) {
          state.currentShop = action.payload;
        }
        state.error = null;
      })
      .addCase(updateShop.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Shop Visibility
      .addCase(updateShopVisibility.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateShopVisibility.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.shops.findIndex(shop => shop.id === action.payload.id);
        if (index !== -1) {
          state.shops[index] = action.payload;
        }
        if (state.currentShop?.id === action.payload.id) {
          state.currentShop = action.payload;
        }
        state.error = null;
      })
      .addCase(updateShopVisibility.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Rate Shop
      .addCase(rateShop.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rateShop.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.shops.findIndex(shop => shop.id === action.payload.id);
        if (index !== -1) {
          state.shops[index] = action.payload;
        }
        if (state.currentShop?.id === action.payload.id) {
          state.currentShop = action.payload;
        }
        state.error = null;
      })
      .addCase(rateShop.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentShop, clearCurrentShop } = shopsSlice.actions;
export { shopsSlice };
export default shopsSlice.reducer;
